import { useCallback } from "react";
import {
  PostgresLayerServiceConfigurationDc,
  PostgresTileCatalogServiceConfigurationDc,
  QueryLayerServiceConfigurationDc,
} from "@evergis/api";

import { updateTileLayer, updateLayer, updateQueryLayer } from "../../resources/layers";
import { useDefaultScaleRange } from "../style/useDefaultScaleRange";
import { LayerInfo } from "../../interfaces/resourceInfo";

type ConfigurationUpdate = Partial<PostgresLayerServiceConfigurationDc>;

type UpdateConfigurationState = {
  updatedLayer?: string;
  error?: Error;
  updating?: boolean;
};

type UseLayerUpdateReturnType = [(config: ConfigurationUpdate) => Promise<void>, UpdateConfigurationState];

// eslint-disable-next-line max-lines-per-function
export const useLayerUpdate = (layer: LayerInfo): UseLayerUpdateReturnType => {
  const defaultScaleRange = useDefaultScaleRange();
  const [update, { loading: updating, error, data: updatedLayer }] = updateLayer.use();
  const [updateQls, { loading: updatingQls, error: errorQls, data: updatedQlsLayer }] = updateQueryLayer.use();
  const [updateTile, { loading: updatingTile, error: errorTile, data: updatedLayerTile }] = updateTileLayer.use();

  const updatePostgresConfiguration = useCallback(
    async (partialConfig: Partial<PostgresLayerServiceConfigurationDc>) => {
      const { name, alias, description, configuration } = layer;

      await update({
        input: {
          defaultScaleRange,
          configuration: {
            ...(configuration as PostgresLayerServiceConfigurationDc),
            name,
            alias,
            description,
            ...partialConfig,
          },
        },
      });
    },
    [defaultScaleRange, layer, update],
  );

  const updateQlsConfiguration = useCallback(
    async (partialConfig: Partial<QueryLayerServiceConfigurationDc>) => {
      const { name, alias, description, configuration } = layer;

      await updateQls({
        input: {
          defaultScaleRange,
          configuration: {
            ...(configuration as QueryLayerServiceConfigurationDc),
            name,
            alias,
            description,
            ...partialConfig,
          },
        },
      });
    },
    [defaultScaleRange, layer, updateQls],
  );

  const updatePostgresTileConfiguration = useCallback(
    async (partialConfig: Partial<PostgresTileCatalogServiceConfigurationDc>) => {
      const { name, alias, description, configuration } = layer;

      await updateTile({
        input: {
          defaultScaleRange,
          configuration: {
            ...(configuration as PostgresTileCatalogServiceConfigurationDc),
            name,
            alias,
            description,
            ...partialConfig,
          },
        },
      });
    },
    [defaultScaleRange, layer, updateTile],
  );

  switch (layer.type) {
    case "PostgresTileLayerService":
      return [
        updatePostgresTileConfiguration,
        { updating: updatingTile, error: errorTile, updatedLayer: updatedLayerTile?.name },
      ];
    case "QueryLayerService":
      return [updateQlsConfiguration, { updating: updatingQls, error: errorQls, updatedLayer: updatedQlsLayer?.name }];
    default:
      return [updatePostgresConfiguration, { updating, error, updatedLayer: updatedLayer?.name }];
  }
};
